import { computed, ref, watch } from 'vue';
import {
  decimalFromStored,
  formatPtBrDecimal,
  maskOptionsFrom,
  parsePtBrDecimal,
  scaleCanonical,
} from '@record-fill';
import type { FormElement, NumberInputProperties } from './form.types';
import { useElementContainerStyles } from './useElementContainerStyles';
import { useFormBuilderStore } from './form-builder.store';
import { useLabelPosition } from './useLabelPosition';
import { useNumberInput } from './useNumberInput';
import { useEditableField } from './useFormBuilderReadonly';

type NumberInputElement = FormElement & { properties: NumberInputProperties };

function displayText(stored: string | undefined, scale: number | null): string {
  const canonical = decimalFromStored(stored);
  return canonical === null ? (stored ?? '') : formatPtBrDecimal(scaleCanonical(canonical, scale));
}

export function useNumberInputElement(getElement: () => NumberInputElement) {
  const formBuilderStore = useFormBuilderStore();
  const properties = computed(() => getElement().properties);
  const mask = computed(() =>
    maskOptionsFrom({
      numberType: properties.value.numberType,
      decimalPlaces: properties.value.decimalPlaces,
      minValue: properties.value.minValue,
      maxValue: properties.value.maxValue,
    })
  );
  const inputValue = ref(displayText(properties.value.value, mask.value.scale));

  // The store keeps the canonical value of what is typed, so a text still being typed, such as
  // "1," or "-", must not be replaced by the display of the value it already stored.
  watch(
    () => properties.value.value,
    (value) => {
      if ((parsePtBrDecimal(inputValue.value) ?? '') === (value ?? '')) return;
      inputValue.value = displayText(value, mask.value.scale);
    }
  );

  // A new scale re-renders even a text that still parses to the stored value: "1,20" must become
  // "1,200" when the places go from 2 to 3.
  watch(
    () => mask.value.scale,
    (scale) => {
      inputValue.value = displayText(properties.value.value, scale);
    }
  );

  const { isLabelLeft, inputClasses, labelClasses, labelStyles } = useLabelPosition(properties);
  const { containerStyles } = useElementContainerStyles(properties, 'NUMBER_INPUT');
  const isEditable = useEditableField(() => properties.value.editable);
  const {
    handleInput: handleNumberInput,
    handleKeydown: handleNumberKeydown,
    handlePaste: handleNumberPaste,
    handleBlur: handleNumberBlur,
  } = useNumberInput(() => ({ mask: mask.value }));

  function persistValue(text: string) {
    inputValue.value = text;

    if (isEditable.value) {
      formBuilderStore.updateCellContentProperty(getElement().id, { value: parsePtBrDecimal(text) ?? '' });
    }
  }

  return {
    inputValue,
    isLabelLeft,
    inputClasses,
    labelClasses,
    labelStyles,
    containerStyles,
    isEditable,
    handleNumberInput,
    handleNumberKeydown,
    handleNumberPaste,
    handleNumberBlur,
    persistValue,
  };
}
